import {
    EntityAccess,
    ScoreTrend,
    SortOrder,
    TableComponentCodes,
} from "../../../config/app-globals.enum";
import { ColumnFilter } from "./ColumnFilter";
import { CustomDate } from "./CustomDate";
import Initials from "./Initials";
import ArrowUpIcon from "../../../assets/images/arrowUp.png";
import ArrowRightIcon from "../../../assets/images/arrowRight.png";
import ArrowDownIcon from "../../../assets/images/arrowDown.png";
import {
    CustomExpandLessIcon,
    CustomExpandMoreIcon,
} from "../CustomIcon/CustomIcon";
import {
    getUserData,
    getUserRBAC,
} from "../../../services/LocalStorageService";
import Marker from "./Marker";
import { preDefinedThemeColor } from "../../../assets/jss/preDefinedColor";
import { Trans } from "react-i18next";
import { RowAction } from "./RowAction";
import { TransformedCell } from "./TransformedCell";
import { getInterpolatedString } from "../../../config/app-global-method";
import moment from "moment";
import CustomTypography from "../CustomTypography/CustomTypography";
import React from "react";

const computeSum = (data) => {
    let sum = 0;
    if (data && data.length) {
        data.forEach((value) => {
            const parsedValue = parseFloat(value);
            if (!isNaN(parsedValue)) {
                sum += parsedValue;
            }
        });
    }
    return sum;
};

const computeAverage = (data) => {
    let average = 0;
    if (data && data.length) {
        const sum = computeSum(data);
        const length = data.length;
        average = sum / length;
    }
    return average;
};

const doSort = (data, order) => {
    if (data && data.length && order) {
        let sortedData;
        let isString = false;
        data.forEach((value) => {
            const parsedValue = parseFloat(value);
            if (isNaN(parsedValue)) {
                isString = true;
            }
        });
        if (order === SortOrder.ASC) {
            sortedData = isString ? data.sort() : data.sort((a, b) => a - b);
        } else if (order === SortOrder.DESC) {
            sortedData = isString ? data.reverse() : data.sort((a, b) => b - a);
        } else {
            sortedData = [...data];
        }
        return sortedData;
    } else {
        return data;
    }
};

const customAggregate = (leafValues, type) => {
    if (leafValues && leafValues.length) {
        let aggregateValue;
        switch (type) {
            case "sum":
                aggregateValue = computeSum(leafValues);
                break;
            case "average":
                aggregateValue = computeAverage(leafValues);
                break;
            case "count":
                aggregateValue =
                    leafValues && leafValues.length ? leafValues.length : 0;
                break;
            case "uniqueCount":
                const uniqueArray = [...new Set(leafValues)];
                aggregateValue =
                    uniqueArray && uniqueArray.length ? uniqueArray.length : 0;
                break;
            case "min":
                const sortedDataAsc = doSort(leafValues, SortOrder.ASC);
                aggregateValue = sortedDataAsc[0];
                break;
            case "max":
                const sortedDataDesc = doSort(leafValues, SortOrder.DESC);
                aggregateValue = sortedDataDesc[0];
                break;
            default:
                aggregateValue = 0;
        }
        return aggregateValue;
    } else {
        return null;
    }
};

const getLabelValue = (aggregate, value, header) => {
    let label = "";
    if (aggregate && aggregate.type && value && header) {
        if (aggregate.label) {
            label = getInterpolatedString(aggregate.label, value);
        } else {
            if (aggregate.type === "uniqueCount") {
                label = `Unique ${header}`;
            } else if (aggregate.type === "average") {
                label = `Avg. ${header}`;
            } else if (aggregate.type === "count") {
                label = `Count of ${header}`;
            } else if (aggregate.type === "sum") {
                label = `Total sum of  ${header}`;
            } else if (aggregate.type === "min") {
                label = `Min ${header}`;
            } else if (aggregate.type === "min") {
                label = `Max ${header}`;
            }
        }
    }
    return label;
};

export const getTableColumns = (
    tableColumnsData,
    tableAccordionEnabled,
    tableGroupByEnabled,
    defaultFilterBy,
    tableSettingView,
    handleCallBack
) => {
    const tableColumns = [];
    const defaultSortedBy = [];
    const defaultGroupedBy = [];
    const defaultHiddenColumns = [];
    const defaultRequiredColumns = [];
    const defaultFilter = [];

    const handleData = (event) => {
        handleCallBack(event);
    }

    if (tableColumnsData && tableColumnsData.length) {
        let sortedColumn = tableColumnsData.sort((a, b) => a.order - b.order);
        for (let i = 0; i < sortedColumn.length; i++) {
            let colSetting = {
                id: `${sortedColumn[i].header.replaceAll(/\s/g, "")}-${i}`,
                Header:
                    sortedColumn[i].header && sortedColumn[i].header.length && sortedColumn[i].isHeaderShow ? (
                        <Trans
                            i18nKey={`COLUMN_NAME_${sortedColumn[i].header.replace(
                                /\s/g,
                                ""
                            )}`}
                        >
                            {sortedColumn[i].header}
                        </Trans>
                    ) : (
                        ""
                    ),
                Filter: ColumnFilter,
                disableFilters: !sortedColumn[i].isFilterable,
                disableSortBy: !sortedColumn[i].isSortable,
                disableGroupBy:
                    sortedColumn[i].disableGroupBy ||
                    tableAccordionEnabled ||
                    !tableGroupByEnabled,
                style: sortedColumn[i].style ? sortedColumn[i].style : null,
                linkTo: sortedColumn[i].linkTo ? sortedColumn[i].linkTo : null,
                isMarkerCell: sortedColumn[i].isMarkerCell,
                isAction: sortedColumn[i].isAction,
                transformationRule: sortedColumn[i].transformation,
                isDate: sortedColumn[i].isDate,
                isTrendIcon: sortedColumn[i].isTrendIcon,
                isRequired: sortedColumn[i].isRequired
            };
            if (sortedColumn[i].isMarkerCell) {
                colSetting["Cell"] = (rowInfo) => {
                    if (rowInfo && rowInfo.cell && rowInfo.cell.value) {
                        const cellValue = parseInt(rowInfo.cell.value);
                        const bgColor =
                            cellValue >= 90
                                ? preDefinedThemeColor.blue
                                : cellValue < 90 && cellValue >= 80
                                    ? preDefinedThemeColor.green
                                    : cellValue < 80 && cellValue >= 70
                                        ? preDefinedThemeColor.orange
                                        : preDefinedThemeColor.red;
                        return <Marker backgroundColor={bgColor} />;
                    }
                    return <></>;
                };
            }
            if (sortedColumn[i].isDate) {
                colSetting["Cell"] = (rowInfo) => {
                    return rowInfo && rowInfo.cell && rowInfo.cell.value ? (
                        <CustomDate date={rowInfo.cell.value} tableView={tableSettingView} eventStatus={rowInfo?.cell?.row?.original?.eventStatus} />
                    ) : null;
                };
            }
            if (sortedColumn[i].propertyName === 'name' && (tableSettingView === TableComponentCodes.REPORTED_EVENTS
                || tableSettingView === TableComponentCodes.APPROVED_REPORTED_EVENTS
                || tableSettingView === TableComponentCodes.REJECTED_REPORTED_EVENTS
                || tableSettingView === TableComponentCodes.BRAKING_REPORTED_EVENTS
                || tableSettingView === TableComponentCodes.CORNERING_REPORTED_EVENTS
                || tableSettingView === TableComponentCodes.ACCELERATION_REPORTED_EVENTS
                || tableSettingView === TableComponentCodes.DISTRACTION_REPORTED_EVENTS
                || tableSettingView === TableComponentCodes.BRAKING_APPROVED_EVENTS
                || tableSettingView === TableComponentCodes.CORNERING_APPROVED_EVENTS
                || tableSettingView === TableComponentCodes.ACCELERATION_APPROVED_EVENTS
                || tableSettingView === TableComponentCodes.DISTRACTION_APPROVED_EVENTS
                || tableSettingView === TableComponentCodes.BRAKING_REJECTED_EVENTS
                || tableSettingView === TableComponentCodes.CORNERING_REJECTED_EVENTS
                || tableSettingView === TableComponentCodes.ACCELERATION_REJECTED_EVENTS
                || tableSettingView === TableComponentCodes.DISTRACTION_REJECTED_EVENTS
                || tableSettingView === TableComponentCodes.MILEAGE_CLAIMED
                || tableSettingView === TableComponentCodes.MILEAGE_APPROVED
                || tableSettingView === TableComponentCodes.MILEAGE_REJECTED
            )) {
                colSetting['Cell'] = (rowInfo) => {
                    return rowInfo && rowInfo?.cell && rowInfo?.cell?.value ? (
                        <>
                            <CustomTypography >{rowInfo.cell.value}</CustomTypography>
                            <CustomTypography style={{ color: '#a3abb6', fontSize: "12px" }}> {(rowInfo?.row?.original?.occurredAt) ? moment(rowInfo?.row?.original?.occurredAt).format("dddd, DD, MMM, YYYY | hh:mm:ss A") : "-"}</CustomTypography>
                        </>
                    ) : null
                }
            }
            if (sortedColumn[i].transformation) {
                colSetting["Cell"] = (rowInfo) => {
                    return rowInfo &&
                        rowInfo.cell &&
                        rowInfo.cell.value &&
                        rowInfo.column &&
                        rowInfo.column.transformationRule ? (
                        <TransformedCell
                            originalData={
                                rowInfo.row &&
                                    rowInfo.row.original &&
                                    sortedColumn[i].propertyName &&
                                    rowInfo.row.original[sortedColumn[i].propertyName]
                                    ? rowInfo.row.original[sortedColumn[i].propertyName]
                                    : null
                            }
                            cellValue={rowInfo.cell.value}
                            transformationRule={rowInfo.column.transformationRule}
                        />
                    ) : null;
                };
            }
            if (sortedColumn[i].aggregate && sortedColumn[i].aggregate.type) {
                colSetting["aggregate"] = (leafValues) =>
                    customAggregate(leafValues, sortedColumn[i].aggregate.type);
                colSetting["Aggregated"] = ({ value }) =>
                    sortedColumn[i].aggregate.labelEnabled === true
                        ? getLabelValue(
                            sortedColumn[i].aggregate,
                            value,
                            sortedColumn[i].header
                        )
                        : value;
            }
            if (sortedColumn[i].isTrendIcon) {
                colSetting["Cell"] = ({ cell: { value } }) => {
                    return (
                        <>
                            {parseInt(value) === ScoreTrend.UP ? (
                                <img alt="" src={ArrowUpIcon} />
                            ) : parseInt(value) === ScoreTrend.NONE ? (
                                <img alt="" src={ArrowRightIcon} />
                            ) : parseInt(value) === ScoreTrend.DOWN ? (
                                <img alt="" src={ArrowDownIcon} />
                            ) : null}
                            {/* {rowInfo && rowInfo.row && rowInfo.row.original ? (
                <>
                  {rowInfo.row.original[sortedColumn[i].propertyName[0]] !==
                  -1 ? (
                    rowInfo.row.original[sortedColumn[i].trendProp] ===
                    ScoreTrend.UP ? (
                      <img
                        alt=""
                        src={ArrowUpIcon}
                        // className={classes.arrowUpIcon}
                      />
                    ) : rowInfo.row.original[sortedColumn[i].trendProp] ===
                      ScoreTrend.NONE ? (
                      <img
                        alt=""
                        src={ArrowRightIcon}
                        // className={classes.arrowRightIcon}
                      />
                    ) : (
                      <img
                        alt=""
                        src={ArrowDownIcon}
                        // className={classes.arrowDownIcon}
                      />
                    )
                  ) : null}
                </>
              ) : null} */}
                        </>
                    );
                };
            }
            if (
                sortedColumn[i].isAction &&
                sortedColumn[i].actionOptions &&
                sortedColumn[i].actionOptions.length
            ) {
                colSetting["Cell"] = (rowInfo) => {
                    return rowInfo &&
                        rowInfo.cell &&
                        rowInfo.cell.column &&
                        rowInfo.cell.column.isAction &&
                        rowInfo.row &&
                        rowInfo.row.canExpand !== true ? (
                        <RowAction
                            rowInfo={rowInfo}
                            options={sortedColumn[i].actionOptions}
                            tableView={tableSettingView}
                            callBackFun={handleData}
                        />
                    ) : null;
                };
            }
            if (sortedColumn[i].propertyName && sortedColumn[i].propertyName.length) {
                if (sortedColumn[i].isAvatar) {
                    colSetting["Cell"] = (rowInfo) => {
                        let avatarInitial = null;
                        if (rowInfo && rowInfo.cell) {
                            let nameForInitial = rowInfo.cell.value && rowInfo.cell.value.length ? [rowInfo.cell.value.trim()] : [];
                            // sortedColumn[i].propertyName.forEach((item, index) => {
                            //   nameForInitial.push(rowInfo.row.original[item]);
                            // });
                            if (nameForInitial && nameForInitial.length) {
                                avatarInitial = (
                                    <Initials
                                        nameArray={nameForInitial}
                                        // firstName={rowInfo.row.original.firstName}
                                        // middleName={rowInfo.row.original.middleName}
                                        // lastName={rowInfo.row.original.lastName}
                                        score={
                                            rowInfo.row &&
                                                rowInfo.row.original &&
                                                rowInfo.row.original.score
                                                ? rowInfo.row.original.score
                                                : null
                                        }
                                    />
                                );
                            }
                        }
                        return avatarInitial;
                    };
                }

                colSetting["accessor"] = (d) => {
                    let accessor = `${d[sortedColumn[i].propertyName]}`;
                    // for (let j = 0; j < sortedColumn[i].propertyName.length; j++) {
                    //   if (j > 0) {
                    //     accessor += ` `;
                    //   }
                    //   accessor += `${d[sortedColumn[i].propertyName[j]]}`;
                    // }
                    return accessor;
                };

                const cellId = `${sortedColumn[i].header.replaceAll(/\s/g, "")}-${i}`;
                if (sortedColumn[i].isSorted) {
                    let sortedBy = {
                        id: cellId,
                        desc: sortedColumn[i].sortedOrder === SortOrder.DESC ? true : false,
                    };
                    defaultSortedBy.push({ ...sortedBy });
                }
                if (sortedColumn[i].isGrouped && tableGroupByEnabled) {
                    defaultGroupedBy.push(cellId);
                }
                if (sortedColumn[i].isHidden) {
                    defaultHiddenColumns.push(cellId);
                }
                if (sortedColumn[i].isRequired) {
                    defaultRequiredColumns.push(cellId);
                }
                if (defaultFilterBy) {
                    for (const [key, value] of Object.entries(defaultFilterBy)) {
                        if (sortedColumn[i].propertyName === key) {
                            const filterObj = {
                                id: cellId,
                                value: value,
                            };
                            defaultFilter.push({ ...filterObj });
                        }
                        // if (sortedColumn[i].propertyName.find((item) => item === key)) {
                        //   const filterObj = {
                        //     id: cellId,
                        //     value: value,
                        //   };
                        //   // filterObj[cellId] = value;
                        //   defaultFilter.push({ ...filterObj });
                        // }
                    }
                }
            }
            tableColumns.push({ ...colSetting });
        }
    }
    return {
        tableColumns,
        defaultSortedBy,
        defaultGroupedBy,
        defaultHiddenColumns,
        defaultFilter,
        defaultRequiredColumns
    };
    /* return [
        {
            Header: "Rank",
            accessor: "rank",
            Filter: ColumnFilter,
            style: {
                header: {},
                column: {},
            },
            aggregate: "uniqueCount",
            Aggregated: ({ value }) => `${value} unique ranks`,
        },
        {
            Header: "First Name",
            accessor: "firstName",
            Filter: ColumnFilter,
            style: {
                header: {},
                column: {},
            },
            aggregate: "count",
            Aggregated: ({ value }) => `${value}  first names`,
        },
        {
            Header: "Last Name",
            accessor: "lastName",
            Filter: ColumnFilter,
            style: {
                header: {},
                column: {},
            },
            aggregate: "count",
            Aggregated: ({ value }) => `${value} last name`,
        },
        {
            Header: "Score",
            accessor: "score",
            Filter: ColumnFilter,
            style: {
                header: {},
                column: {},
            },
            aggregate: "count",
            Aggregated: ({ value }) => `${value} Score`,
        },
    ]; */
};

export const getExpander = (
    tableAccordionProps,
    isAccordionEnabled,
    tableGroupByEnabled, props
) => {
    const userData = getUserData();
    const expander = {
        // Build our expander column
        id: "accordionExpander", // Make sure it has an ID
        disableSortBy: true,
        style: {
            header: {
                width: 75,
            },
            column: {},
        },
        Filter: ColumnFilter,
        disableFilters: true,
        Cell: (tableData) =>
        // Use the row.canExpand and row.getToggleRowExpandedProps prop getter
        // to build the toggle for expanding a row
        // row.canExpand
        {
            const accordionComponentEntity = getUserRBAC(
                tableAccordionProps.access.viewEntity, props
            );
            return isAccordionEnabled &&
                !tableGroupByEnabled &&
                accordionComponentEntity &&
                accordionComponentEntity.isAccessible === EntityAccess.ALLOWED &&
                (accordionComponentEntity.isGroupScope === EntityAccess.ALLOWED ||
                    (tableData.row.original[tableAccordionProps.access.accessUserId] &&
                        userData &&
                        (userData.id ===
                            tableData.row.original[
                            tableAccordionProps.access.accessUserId
                            ]) || props.match.params && props.match.params.impersonationid === tableData.row.original[tableAccordionProps.access.accessUserId])) ? (
                // userData.id ===
                //   tableData.row.original[
                //     tableAccordionProps.access.accessUserId
                //   ]
                <span
                    {...tableData.row.getToggleRowExpandedProps([
                        {
                            onClick: (e) => {
                                if (!tableData.row.isExpanded) {
                                    tableData.toggleAllRowsExpanded(false);
                                    const elem = document.getElementById(
                                        `praisoTableRow_${tableData.row.id}`
                                    );
                                    if (elem) {
                                        elem.scrollIntoView({ behavior: "smooth" });
                                    }
                                }
                                tableData.row.toggleRowExpanded();
                            },
                        },
                    ])}
                >
                    {tableData.row.isExpanded ? (
                        <CustomExpandLessIcon />
                    ) : (
                        <CustomExpandMoreIcon />
                    )}
                </span>
            ) : null;
        },
    };
    return expander;
};

// export const COLUMNS = [
//   {
//     Header: "Rank",
//     accessor: "rank",
//     Filter: ColumnFilter,
//     style: {
//       header: {},
//       column: {},
//     },
//     aggregate: "uniqueCount",
//     Aggregated: ({ value }) => `${value} unique ranks`,
//   },
//   {
//     Header: "First Name",
//     accessor: "firstName",
//     Filter: ColumnFilter,
//     style: {
//       header: {},
//       column: {},
//     },
//     aggregate: "count",
//     Aggregated: ({ value }) => `${value}  first names`,
//   },
//   {
//     Header: "Last Name",
//     accessor: "lastName",
//     Filter: ColumnFilter,
//     style: {
//       header: {},
//       column: {},
//     },
//     aggregate: "count",
//     Aggregated: ({ value }) => `${value} last name`,
//   },
//   {
//     Header: "Score",
//     accessor: "score",
//     Filter: ColumnFilter,
//     style: {
//       header: {},
//       column: {},
//     },
//     aggregate: "count",
//     Aggregated: ({ value }) => `${value} Score`,
//   },
// ];
